/**
 * Pantalla principal: mapa, lista de puntos y ficha del punto.
 *
 * Es donde se juntan las tres piezas delicadas del proyecto: el seguimiento de
 * la posicion, el disparo del video en la primera visita y el acceso a la
 * pregunta. El orden importa y esta pensado asi:
 *
 *  1. La persona pulsa un boton para empezar. Ese gesto sirve para dos cosas a
 *     la vez: pedir permiso de ubicacion y mantener la pantalla encendida. Un
 *     solo toque, no dos. De paso, en Chrome y Firefox deja a la pagina
 *     reproducir con sonido mas tarde; en iOS no basta (ver `lib/reproducir.ts`).
 *  2. El seguimiento detecta la entrada en el radio y avisa al servidor.
 *  3. Solo si el servidor dice que es la primera visita se abre la ficha con el
 *     video en marcha: con sonido si el navegador lo deja, en silencio si no.
 *
 * Lo que ve cada uno, que antes era lo mismo para todos:
 *
 *  - Sin sesion: una presentacion de que es esto y como se entra. Antes se caia
 *    directamente en un mapa con una barra de ubicacion encima, sin una linea
 *    que dijera de que iba la aplicacion.
 *  - Con sesion y sin empezar: el mismo bloque, pero con el boton de arranque.
 *  - Andando: una barra compacta con el progreso y el estado del GPS, que es lo
 *    unico que interesa mirar con el movil en la mano.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatearDistancia, type PuntoPublico } from '@recorregut/shared';
import { useProximidad } from '@/hooks/useProximidad.ts';
import { useGeolocalizacion } from '@/hooks/useGeolocalizacion.ts';
import { usePantallaEncendida } from '@/hooks/usePantallaEncendida.ts';
import { usePuntos, useRecorrido, useSesion } from '@/api/consultas.ts';
import { MapaRecorrido } from '@/componentes/MapaRecorrido.tsx';
import { ListaPuntos } from '@/componentes/ListaPuntos.tsx';
import { PanelDeslizante } from '@/componentes/PanelDeslizante.tsx';
import { FichaPunto } from '@/componentes/FichaPunto.tsx';
import { MensajeError } from '@/componentes/MensajeError.tsx';
import { useAnuncios } from '@/componentes/Anuncio.tsx';
import { etiquetaFormato } from '@/i18n';

export function Recorrido() {
  const { t } = useTranslation();
  const { anunciar } = useAnuncios();

  const consultaSesion = useSesion();
  const consultaPuntos = usePuntos();
  const consultaRecorrido = useRecorrido(consultaSesion.data != null);

  const ubicacion = useGeolocalizacion();
  const pantalla = usePantallaEncendida();

  const [puntoAbierto, setPuntoAbierto] = useState<PuntoPublico | null>(null);
  const [reproduccionAutomatica, setReproduccionAutomatica] = useState(false);
  const [recorridoIniciado, setRecorridoIniciado] = useState(false);

  const puntos = useMemo(() => consultaPuntos.data ?? [], [consultaPuntos.data]);
  const conSesion = consultaSesion.data != null;

  const { llegada, descartarLlegada, dentroDeRadio, distancias, candidatos, elegirPunto } =
    useProximidad(
    puntos,
    ubicacion.posicion,
    recorridoIniciado && conSesion,
  );

  const visitados = useMemo(
    () => new Set(consultaRecorrido.data?.visitados ?? []),
    [consultaRecorrido.data],
  );
  const respondidos = useMemo(
    () => new Set((consultaRecorrido.data?.respondidos ?? []).map((fila) => fila.punto_id)),
    [consultaRecorrido.data],
  );
  const puntosDelEvento = useMemo(
    () => new Set((consultaRecorrido.data?.puntos_del_evento ?? []).map((fila) => fila.punto_id)),
    [consultaRecorrido.data],
  );

  /**
   * Un solo gesto del usuario para arrancar todo. Las dos llamadas cuelgan de
   * este mismo manejador porque el permiso de ubicacion y el bloqueo de pantalla
   * solo se conceden dentro de un evento de usuario.
   */
  const empezar = useCallback(async () => {
    ubicacion.activar();
    await pantalla.activar();
    setRecorridoIniciado(true);
    anunciar(t('geolocalitzacio.cercant'));
  }, [ubicacion, pantalla, anunciar, t]);

  /** Llegada a un punto: se abre la ficha con el video en marcha. */
  useEffect(() => {
    if (!llegada) return;
    setPuntoAbierto(llegada.punto);
    setReproduccionAutomatica(true);
    anunciar(`${llegada.punto.nombre}. ${t('punt.arribat')}`);
    descartarLlegada();
  }, [llegada, anunciar, t, descartarLlegada]);

  const abrirPunto = useCallback((punto: PuntoPublico) => {
    setPuntoAbierto(punto);
    // Apertura manual: el video no arranca solo, lo pide la persona.
    setReproduccionAutomatica(false);
  }, []);

  if (consultaPuntos.isError) {
    return (
      <div className="contenedor py-8">
        <MensajeError
          error={consultaPuntos.error}
          alReintentar={() => void consultaPuntos.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {recorridoIniciado ? (
        <BarraDeMarcha
          estado={ubicacion.estado}
          precision={ubicacion.posicion?.precision ?? null}
          pantallaActiva={pantalla.activo}
          respondidos={respondidos.size}
          totalDelEvento={puntosDelEvento.size}
          puntosTotales={consultaRecorrido.data?.puntos_totales ?? 0}
          conSesion={conSesion}
        />
      ) : (
        <PanelDeBienvenida conSesion={conSesion} alEmpezar={() => void empezar()} />
      )}

      {/*
        Varios puntos igual de cerca: decide la persona, que es quien los tiene
        delante. Solo sale cuando la diferencia entre ellos es menor que el error
        tipico del GPS urbano; con uno claramente mas cerca, no se pregunta.
      */}
      {candidatos.length > 1 ? (
        <section
          role="status"
          className="border-b border-or-500/40 bg-or-50 px-4 py-3"
          aria-label={t('punt.variosTitol')}
        >
          <p className="text-sm font-medium text-tinta-900">{t('punt.variosTitol')}</p>
          <p className="mt-0.5 text-sm text-tinta-700">{t('punt.variosAjuda')}</p>
          <ul className="mt-2 flex flex-wrap gap-2">
            {candidatos.map((punto) => (
              <li key={punto.id}>
                <button
                  type="button"
                  className="boton-secundario"
                  onClick={() => {
                    elegirPunto(punto);
                  }}
                >
                  {punto.nombre}
                  <span className="ml-1.5 text-tinta-500">
                    {t('punt.distancia', {
                      distancia: formatearDistancia(
                        distancias.get(punto.id) ?? 0,
                        etiquetaFormato(),
                      ),
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/*
        `relative` con el mapa en `absolute inset-0` y no un simple contenedor:
        el mapa se dibujaba con altura CERO. Leaflet pide `height: 100%`, que se
        resuelve contra la ALTURA del padre, y este solo declaraba `min-height`,
        que no cuenta como altura para un porcentaje. Resultado: cargaba las
        teselas y los marcadores, y no se veia nada. Medido el 08/09/2026 en
        movil y en escritorio: contenedor 378 px, mapa 0 px.
      */}
      <div className="relative min-h-[42dvh] flex-1 border-y border-crema-200">
        <MapaRecorrido
          puntos={puntos}
          posicion={ubicacion.posicion}
          visitados={visitados}
          respondidos={respondidos}
          distancias={distancias}
          alAbrirPunto={abrirPunto}
          centrarEnPosicion={recorridoIniciado}
        />
      </div>

      {/* Version accesible del mapa, siempre presente */}
      <section
        aria-labelledby="titulo-llista-punts"
        className="max-h-[45dvh] overflow-y-auto bg-white"
      >
        <div
          className="sticky top-0 z-10 flex items-baseline justify-between gap-3
                     border-b border-crema-200 bg-white px-4 py-3 sm:px-6"
        >
          <h2 id="titulo-llista-punts" className="titulo-ficha">
            {t('punt.llistaTitol')}
          </h2>
          {puntos.length > 0 ? (
            <p className="text-sm text-tinta-500">
              {new Intl.NumberFormat(etiquetaFormato()).format(puntos.length)}
            </p>
          ) : null}
        </div>
        <p className="sr-only">{t('mapa.descripcio')}</p>

        {consultaPuntos.isPending ? (
          <p role="status" className="px-4 py-6 text-tinta-500 sm:px-6">
            {t('app.carregant')}
          </p>
        ) : (
          <ListaPuntos
            puntos={puntos}
            visitados={visitados}
            respondidos={respondidos}
            dentroDeRadio={dentroDeRadio}
            distancias={distancias}
            alAbrirPunto={abrirPunto}
          />
        )}
      </section>

      <PanelDeslizante
        abierto={puntoAbierto !== null}
        titulo={puntoAbierto?.nombre ?? ''}
        alCerrar={() => {
          setPuntoAbierto(null);
          setReproduccionAutomatica(false);
        }}
      >
        {puntoAbierto ? (
          <FichaPunto
            punto={puntoAbierto}
            reproduccionAutomatica={reproduccionAutomatica}
            visitado={visitados.has(puntoAbierto.id)}
            eventoEnCurso={consultaRecorrido.data?.en_curso ?? false}
            perteneceAlEvento={puntosDelEvento.has(puntoAbierto.id)}
            distancia={distancias.get(puntoAbierto.id)}
          />
        ) : null}
      </PanelDeslizante>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Lo primero que se ve al abrir la aplicacion.
 *
 * Antes aqui habia un parrafo sobre el permiso de ubicacion y un boton, sin una
 * sola linea que dijera que es esto. Quien escanea un cartel en la calle y abre
 * el enlace merece saber en que consiste antes de que se le pida el GPS.
 */
function PanelDeBienvenida({
  conSesion,
  alEmpezar,
}: {
  conSesion: boolean;
  alEmpezar: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-white">
      {/*
        Medido en un movil de 844 px de alto: con el titular a `text-3xl` y el
        aire por defecto, este bloque se comia la pantalla entera y el mapa
        quedaba debajo del pliegue, asi que la aplicacion abria en un muro de
        texto. El titular arranca mas bajo y sube en pantallas grandes, y el
        aire es el justo para que el mapa asome.
      */}
      <div className="contenedor py-5 sm:py-8">
        <p className="antetitulo">{t('recorregut.antetitol')}</p>
        <h1 className="mt-1.5 text-[1.6rem] leading-tight font-semibold text-tinta-900 sm:text-4xl">
          {t('recorregut.titol')}
        </h1>
        <div className="filete mt-2.5" />

        <p className="mt-3 max-w-prose text-[0.9375rem] leading-relaxed text-tinta-700 sm:text-base">
          {t('recorregut.intro')}
        </p>

        <button type="button" className="boton-primario mt-4 w-full sm:w-auto" onClick={alEmpezar}>
          {t('geolocalitzacio.activar')}
        </button>
        <p className="mt-2 max-w-prose text-sm text-tinta-500">
          {t('geolocalitzacio.explicacio')}
        </p>

        {/*
          Sin sesion se puede mirar el mapa y los videos, pero no responder. Se
          dice aqui y no cuando ya se ha llegado a un punto y la pregunta no
          aparece, que es donde se descubria antes.
        */}
        {!conSesion ? (
          <p className="aviso mt-4 max-w-prose">{t('sessio.sensesessioDetall')}</p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Barra de marcha: lo unico que se mira con el movil en la mano.
 *
 * Tres datos y ninguno mas: cuanto llevas, cuantos puntos tienes y si el GPS
 * responde. Va pegada arriba porque durante el recorrido la pantalla se mira de
 * reojo, no se lee.
 */
function BarraDeMarcha({
  estado,
  precision,
  pantallaActiva,
  respondidos,
  totalDelEvento,
  puntosTotales,
  conSesion,
}: {
  estado: ReturnType<typeof useGeolocalizacion>['estado'];
  precision: number | null;
  pantallaActiva: boolean;
  respondidos: number;
  totalDelEvento: number;
  puntosTotales: number;
  conSesion: boolean;
}) {
  const { t } = useTranslation();
  const formato = etiquetaFormato();
  const numero = new Intl.NumberFormat(formato);

  const mensajes: Record<typeof estado, string> = {
    inactiva: t('geolocalitzacio.activar'),
    solicitando: t('geolocalitzacio.cercant'),
    activa: t('geolocalitzacio.activa'),
    denegada: t('geolocalitzacio.denegada'),
    no_disponible: t('geolocalitzacio.noDisponible'),
    error: t('geolocalitzacio.error'),
  };

  const problema = estado === 'denegada' || estado === 'no_disponible' || estado === 'error';
  const hayProgreso = conSesion && totalDelEvento > 0;
  const porcentaje = hayProgreso ? Math.round((respondidos / totalDelEvento) * 100) : 0;

  return (
    <div className="sticky top-0 z-20 bg-white">
      <div className="contenedor py-3">
        {/*
          El titulo de la pantalla, para lectores de pantalla. Visible ya esta
          el de la bienvenida, pero ese desaparece al empezar el recorrido y
          dejaba la pantalla sin ningun h1: quien navega por encabezados se
          quedaba sin saber donde esta justo cuando el juego ya ha empezado.
        */}
        <h1 className="sr-only">{t('recorregut.titol')}</h1>
        {hayProgreso ? (
          <div className="mb-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium text-tinta-900">
                {/*
                  `count` decide la forma del plural y las otras dos variables
                  llevan el numero ya formateado con Intl. Con una sola variable
                  habria que elegir entre plural correcto o separador de miles
                  del idioma, y hacen falta las dos cosas.
                */}
                {t('recorregut.progres', {
                  count: totalDelEvento,
                  fets: numero.format(respondidos),
                  total: numero.format(totalDelEvento),
                })}
              </p>
              <p className="font-titular text-lg leading-none font-semibold text-roig-700">
                {t('recorregut.puntsAcumulats', {
                  count: puntosTotales,
                  punts: numero.format(puntosTotales),
                })}
              </p>
            </div>

            {/*
              La barra es decorativa: el mismo dato ya esta escrito arriba en
              texto. Marcarla con role="progressbar" haria que un lector de
              pantalla lo repitiera dos veces seguidas.
            */}
            <div
              aria-hidden="true"
              className="mt-2 h-1.5 overflow-hidden rounded-full bg-crema-200"
            >
              <div
                className="h-full rounded-full bg-verd-600 transition-[width] duration-500"
                style={{ width: `${porcentaje}%` }}
              />
            </div>
          </div>
        ) : null}

        {/*
          Los cambios de estado de la ubicacion se anuncian, porque condicionan
          que el juego funcione o no.
        */}
        <p
          role="status"
          className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm ${
            problema ? 'font-medium text-roig-700' : 'text-tinta-500'
          }`}
        >
          <span
            aria-hidden="true"
            className={`size-2 shrink-0 rounded-full ${
              problema
                ? 'bg-roig-600'
                : estado === 'activa'
                  ? 'bg-verd-600'
                  : 'bg-tinta-400'
            }`}
          />
          <span>{mensajes[estado]}</span>
          {precision !== null && estado === 'activa' ? (
            <span>{t('mapa.precisio', { metres: `${Math.round(precision)} m` })}</span>
          ) : null}
          {pantallaActiva ? (
            /* tinta-500 y no tinta-400: el 400 es color de borde (3,5:1) y como
               texto se queda por debajo del 4,5:1 que pide WCAG 1.4.3. */
            <span className="text-tinta-500">{t('geolocalitzacio.pantallaEncesa')}</span>
          ) : null}
        </p>
      </div>
      <div className="h-px bg-crema-200" />
    </div>
  );
}
